import { createApi } from '@reduxjs/toolkit/query/react';
import { ApiPaginationResponse, ApiResponse } from 'types';

import { apiBaseQuery } from 'modules/apiBaseQuery';
import { FetchGoCertificatesShortenPayload } from 'modules/eecs-go/eecs-certificates';

import {
  AddForwardPayload,
  CheckForwardAvailableCertificatesPayload,
  CheckForwardAvailableCertificatesResponse,
  CloneForwardPayload,
  DeleteForwardAssignmentPayload,
  DeleteForwardPayload,
  FetchForwardsPayload,
  ForwardAssignBundlePayload,
  IForward,
  IForwardShorten,
  UpdateForwardModePayload,
  UpdateForwardPayload,
} from '../types/eecs-forwards.types';

export const eecsForwardApi = createApi({
  reducerPath: 'eecsForwardApi',
  baseQuery: apiBaseQuery(`${process.env.REACT_APP_API_URL}/v2/api/admin/necs`),
  endpoints: (builder) => ({
    fetchForwards: builder.query<ApiPaginationResponse<IForward[]>, FetchForwardsPayload>({
      query: (payload) => ({
        url: `/forward-certificates`,
        method: 'GET',
        params: payload,
      }),
    }),

    fetchShortenForwards: builder.query<ApiPaginationResponse<IForwardShorten[]>, FetchGoCertificatesShortenPayload>({
      query: (payload) => ({
        url: `/forward-certificates/select`,
        method: 'GET',
        params: payload,
      }),
    }),

    addForward: builder.mutation<ApiResponse<IForward>, AddForwardPayload>({
      query: (payload) => ({
        url: `/forward-certificates`,
        method: 'POST',
        body: payload,
      }),
    }),

    cloneForward: builder.query<ApiResponse<IForward>, CloneForwardPayload>({
      query: (payload) => ({
        url: `/forward-certificates/${payload.id}/clone`,
        method: 'GET',
      }),
    }),

    updateForward: builder.mutation<ApiResponse<IForward>, UpdateForwardPayload>({
      query: (payload) => ({
        url: `/forward-certificates/${payload.id}`,
        method: 'PUT',
        body: payload,
      }),
    }),

    updateForwardMode: builder.mutation<ApiResponse<IForward>, UpdateForwardModePayload>({
      query: (payload) => ({
        url: `/forward-certificates/${payload.id}/mode`,
        method: 'PUT',
        body: payload,
      }),
    }),

    deleteForward: builder.mutation<ApiResponse<any>, DeleteForwardPayload>({
      query: (payload) => ({
        url: `/forward-certificates/${payload.id}`,
        method: 'Delete',
      }),
    }),

    assignBundle: builder.mutation<ApiResponse<IForward>, ForwardAssignBundlePayload>({
      query: (payload) => ({
        url: `/forward-deliveries/${payload.delivery_id}}/fulfillment-delivery`,
        method: 'PUT',
        body: payload,
      }),
    }),

    deleteAssignment: builder.query<ApiResponse<IForward>, DeleteForwardAssignmentPayload>({
      query: (payload) => ({
        url: `/forward-deliveries/${payload.delivery_id}/assignment/${payload.assignment_id}`,
        method: 'DELETE',
      }),
    }),

    checkAvailableCertificates: builder.query<
      ApiResponse<CheckForwardAvailableCertificatesResponse>,
      CheckForwardAvailableCertificatesPayload
    >({
      query: (payload) => ({
        url: `/forward-deliveries/${payload.delivery_id}/has-certificates-to-fulfill`,
        method: 'GET',
      }),
    }),
  }),
});

export const {
  useAddForwardMutation,
  useDeleteForwardMutation,
  useUpdateForwardMutation,
  useLazyFetchForwardsQuery,
  useLazyCloneForwardQuery,
  useLazyDeleteAssignmentQuery,
  useAssignBundleMutation,
  useUpdateForwardModeMutation,
  useLazyFetchShortenForwardsQuery,
  useLazyCheckAvailableCertificatesQuery,
} = eecsForwardApi;
